import { initializer, inject, injectable } from "@launchtray/tsyringe-async";
import { ICanvasScaler } from "../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../shared/async/PromiseCompletionSource";
import { Viewable } from "../../../../scene/viewable/Viewable";
import { CommonResourcesPackage } from "../../common/resources/CommonResourcesPackage";
import { IDiceRollOfferController } from "./controller/IDiceRollOfferController";
import { IDiceRollsOfferModel } from "./model/IDiceRollsOfferModel";
import { DiceRollsOfferPopupView } from "./view/DiceRollsOfferPopupView";
import { Web3Model } from "../../../../web3/models/Web3Model";
import { ILogger } from "../../../logger/ILogger";
import { StarsResourcePackage } from "../../common/resources/StarsResourcePackage";
import { DiceRollOfferResourcePackage } from "./resources/DiceRollOfferResourcePackage";
import { AsyncOperationNotification } from "../../common/components/AsyncOperationNotification";
import { DicesResourcePackage } from "../../common/resources/DicesResourcePackage";
import { IInventory } from "../../../../web3/models/IInventory";
import { IAnalyticProviderWrapper } from "../../../../analytics/IAnalyticProviderWrapper";
import { VirtualScreenEvent } from "../../../../analytics/events/VirtualScreenEvent";

@injectable()
export class DiceRollsOfferPopupScene extends Viewable<void> {
    private readonly commonResources: CommonResourcesPackage;
    private readonly diceRollsOfferModel: IDiceRollsOfferModel;
    private readonly diceRollsOfferController: IDiceRollOfferController;
    private readonly canvasScaler: ICanvasScaler;
    private readonly web3Model: Web3Model;
    private readonly inventory: IInventory;
    private readonly logger: ILogger;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly analyticsProvider: IAnalyticProviderWrapper;

    public constructor(
        commonResources: CommonResourcesPackage,
        @inject("IDiceRollsOfferModel") diceRollsOfferModel: IDiceRollsOfferModel,
        @inject("IDiceRollOfferController") diceRollsOfferController: IDiceRollOfferController,
        @inject("ICanvasScaler") canvasScaler: ICanvasScaler,
        web3Model: Web3Model,
        @inject("ILogger") logger: ILogger,
        asyncOperationNotification: AsyncOperationNotification,
        @inject("IInventory") inventory: IInventory,
        @inject("IAnalyticProviderWrapper") analyticsProvider: IAnalyticProviderWrapper
    ) {
        super();
        this.commonResources = commonResources;
        this.diceRollsOfferModel = diceRollsOfferModel;
        this.diceRollsOfferController = diceRollsOfferController;
        this.canvasScaler = canvasScaler;
        this.web3Model = web3Model;
        this.inventory = inventory;
        this.logger = logger;
        this.asyncOperationNotification = asyncOperationNotification;
        this.analyticsProvider = analyticsProvider;
    }

    @initializer()
    public async initialization(): Promise<void> {
        const starsResources = new StarsResourcePackage();
        const diceRollsOfferResources = new DiceRollOfferResourcePackage();
        const dicesResources = new DicesResourcePackage();
        try {
            this.asyncOperationNotification.show();
            await diceRollsOfferResources.load();
            await starsResources.load();
            await dicesResources.load();
            await this.web3Model.waitSmartWalletConnection();
            await this.inventory.update();
            this.asyncOperationNotification.hide();
            const pcs = new PromiseCompletionSource<void>();
            this.view = new DiceRollsOfferPopupView(
                pcs,
                this.commonResources,
                starsResources,
                dicesResources,
                diceRollsOfferResources,
                this.canvasScaler,
                this.diceRollsOfferModel,
                this.diceRollsOfferController,
                this.web3Model,
                this.analyticsProvider,
                this.logger
            );
            this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Display run out of coins offer", this.analyticsProvider.currentEventIndex));
            pcs.result.then(() => {
                starsResources.destroy();
                diceRollsOfferResources.destroy();
                dicesResources.destroy();
                this.operationControls.complete();
            }, (reason: any) => {
                starsResources.destroy();
                diceRollsOfferResources.destroy();
                dicesResources.destroy();
                this.operationControls.error(reason);
            });
        } catch (error: unknown) {
            if (starsResources?.loaded) {
                starsResources.destroy();
            }
            if (diceRollsOfferResources?.loaded) {
                diceRollsOfferResources.destroy();
            }
            if (dicesResources?.loaded) {
                dicesResources.destroy();
            }
            this.logger.error(error);
            this.asyncOperationNotification.hide();
            this.operationControls.error(error);
        }
    }
}
